/**
 * useEditOnboardingV2 Hook
 *
 * Updates V2 onboarding metadata without importing legacy screen services.
 * V2 screen documents remain in screensV2; locale-specific StepNode content
 * migration is handled by dedicated V2 editor flows, not V1 ScreenGraph remap.
 */

import { useState, useCallback, useRef } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { EditOnboardingInput, UseEditOnboardingResult } from '../types/onboardings.types';

export interface UseEditOnboardingV2Options {
  projectId: string;
}

export function useEditOnboardingV2(options: UseEditOnboardingV2Options): UseEditOnboardingResult {
  const { projectId } = options;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onboardingServiceRef = useRef(new OnboardingService());

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateOnboarding = useCallback(async (
    onboardingId: string,
    input: EditOnboardingInput
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      const onboarding = await onboardingService.getOnboarding(projectId, onboardingId);
      if (!onboarding) {
        throw new Error(`Onboarding ${onboardingId} not found`);
      }

      const latestVersion = await onboardingService.getLatestVersion(projectId, onboardingId);
      if (!latestVersion) {
        throw new Error('No version found for this onboarding');
      }

      await onboardingService.updateOnboarding(projectId, onboardingId, {
        name: input.name,
        defaultLanguage: input.defaultLanguage,
        languages: input.languages,
        platform: input.platform,
      });

      await onboardingService.updateVersion(projectId, onboardingId, latestVersion.id, {
        screensGraph: {
          ...latestVersion.screensGraph,
          defaultLanguage: input.defaultLanguage,
          languages: input.languages,
          screens: {},
          schemaVersion: latestVersion.screensGraph?.schemaVersion || '2.0.0',
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update onboarding';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  return {
    updateOnboarding,
    loading,
    error,
    clearError,
  };
}
